import { Injectable, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { AngularPlugin } from "@microsoft/applicationinsights-angularplugin-js";
import { ClickAnalyticsPlugin } from "@microsoft/applicationinsights-clickanalytics-js";
import { ITelemetryItem } from "@microsoft/applicationinsights-web";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { Subject, takeUntil } from "rxjs";
import { environment } from "src/environments/environment";

import { LoginStatus } from "../models/Enums";
import { AuthService } from "./auth.service";
import { RegionService } from "./region.service";

const App_Insight_Role_Name = "wimt-client";
const No_User_Azure_Id = "<no-user-id>";
const No_User_Email = "<no-user-email>";
const No_User_Role = "<no-user-role>";
const Unknown_Region = "unknown_region";

@Injectable({ providedIn: "root" })
export class AppInsightsService implements OnDestroy {
  private appInsights: ApplicationInsights;
  private readonly _destroying$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private router: Router,
    private regionService: RegionService,
  ) {
    if (this.canInitializeAppTelemetry() === false) {
      return;
    }
    this.initializeAppTelemetry();
    this.handleLoginChange();
  }

  private handleLoginChange(): void {
    this.authService.isUserProfileSet$
      .pipe(takeUntil(this._destroying$))
      .subscribe((isLoggedIn) => {
        switch (isLoggedIn) {
          case LoginStatus.LoggedIn:
            this.setAdditionalProperties();
            break;
          case LoginStatus.LoggedOut:
            this.appInsights.clearAuthenticatedUserContext();
        }
      });
  }

  private initializeAppTelemetry(): void {
    const angularPlugin = new AngularPlugin();

    // *** Add the Click Analytics plug-in. ***
    const clickPluginInstance = new ClickAnalyticsPlugin();
    const clickPluginConfig = {
      autoCapture: true,
      dataTags: { useDefaultContentNameOrId: true },
    };

    this.appInsights = new ApplicationInsights({
      config: {
        enableAutoRouteTracking: true,
        enableCorsCorrelation: true,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        connectionString: environment.appInsights.connectionString,
        extensions: [angularPlugin, clickPluginInstance],
        extensionConfig: {
          [angularPlugin.identifier]: {
            router: this.router,
          },
          // *** Add the Click Analytics plug-in. ***
          [clickPluginInstance.identifier]: clickPluginConfig,
        },
      },
    });

    const regionTelemetry = (envelope: ITelemetryItem) => {
      // set the role for the given instance of AI telemetry
      envelope.tags["ai.cloud.role"] = App_Insight_Role_Name;
      return true;
    };

    const appVersionTelemetry = (envelope: ITelemetryItem) => {
      envelope.tags["ai.application.ver"] = environment.appVersion;
      return true;
    };

    const userInfoTelemetry = (envelope: ITelemetryItem): boolean | void => {
      envelope.data["userAzureId"] = this.authService.user?.azureObjectId || No_User_Azure_Id;
      envelope.data["userRole"] = this.authService.user?.role || No_User_Role;
      return true;
    };

    this.appInsights.addTelemetryInitializer(regionTelemetry);
    this.appInsights.addTelemetryInitializer(userInfoTelemetry);
    this.appInsights.addTelemetryInitializer(appVersionTelemetry);

    this.appInsights.loadAppInsights();
    this.setAdditionalProperties();
  }

  private setAdditionalProperties(): void {
    const userEmail = this.authService?.user?.email || No_User_Email;
    // we group users by region and keep this information in `accountId` p`roperty of
    // user. This property servers as a custom grouping mean in the application.
    const region = this.regionService.getCurrentRegion()?.region || Unknown_Region;

    // store the userId across the pageView, that's why stored the value in cookie
    this.appInsights.setAuthenticatedUserContext(userEmail, region, true);
  }

  private canInitializeAppTelemetry(): boolean {
    const appInsights = environment.appInsights;

    if (appInsights.disabled) {
      return false;
    }

    if (!appInsights?.connectionString) {
      throw new Error("Application Insight is enabled, but the connection string is not provided.");
    }

    return true;
  }

  public logException(exception: Error, severityLevel?: number): void {
    if (environment.appInsights.disabled) {
      return;
    }

    this.appInsights.trackException({
      exception: exception,
      severityLevel: severityLevel,
    });
  }

  public ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
